import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { useAuth } from '../../context/AuthContext';
import { useAccount } from '../../context/AccountContext';
import { useAuthPage } from '../../hooks/useAuthPage';
import {
  CREATE_COWORK_MCP_CONNECTOR,
  CREATE_SETUP_INTENT,
  ROTATE_COWORK_MCP_CREDENTIALS,
  SIGNUP_MUTATION,
  UPDATE_PUBLIC_SITE,
} from '../../graphql/mutations';
import { AGENT_GUIDES, AgentGuide, ConnectionValue, VALUE_LABELS } from '../cms/agentGuides';
import { AgentRules, useAccountAgents } from '../settings/accountAgents';
import { RequestAccess } from './RequestAccess';
import { StarterStep } from './StarterStep';
import { SwallowMark } from '../common/SwallowMark';
import { TinycmsWordmark } from '../common/TinycmsWordmark';
import { REQUEST_EMAIL_CODE, RESEND_SECONDS } from './TinycmsLogin';
import {
  BillingInterval,
  billingLine,
  chargeLine,
  money,
  planPrice,
  TinycmsPlan,
  toInterval,
  useTinycmsPlans,
} from './tinycmsPlans';
import { Loader } from '../common/PageLoader';
import { loginPathFor, takeReturnPath } from '../../context/signedInState';
import { copyToClipboard } from '../../lib/copyToClipboard';

/**
 * The tinycms signup, from the plan to a connected agent, as one journey
 * ("tinycms UX optimization review v3.zip", tinycms Signup Journey):
 *
 *   Plan → Account → Payment (paid plans only) → Site address → Starting point → Agent
 *
 * Signup is open; an invite code is optional. "Have an invite code?" sits under
 * Plan and Account (the marketing site's pricing links start at Account). A
 * code that grants a complimentary plan settles the plan too, so Plan and
 * Payment drop out — no card. A code without a grant changes nothing else.
 *
 * The account is created at the end of Account (Free) or Payment (paid);
 * Site address and Agent are the old first-run setup, now the tail of
 * signup. Someone signed in whose site has no address yet lands back here
 * (AppShell sends them), at Site address.
 *
 * ?plan=personal&interval=month preselects a paid plan and starts at Account
 * (the pricing page links this way). ?plan=free doesn't: "Start free" links land
 * on Plan like any other visit, so Free is chosen there on purpose.
 *
 * The code is typed, or comes with the link (?invite=, checked before the page
 * shows). Account is the site name and email, then its own "Check your email"
 * screen for the 6-digit
 * code (the account is made with that proof). There is no password: people
 * sign in with emailed codes, and can add a password later in the app.
 */

type Step = 'plan' | 'account' | 'payment' | 'address' | 'starter' | 'agent';

const STEP_LABELS: Record<Step, string> = {
  plan: 'Plan',
  account: 'Account',
  payment: 'Payment',
  address: 'Site URL',
  starter: 'Starting point',
  agent: 'Agent',
};

export const SIGNUP_STATE = gql`
  query TinycmsSignupState($id: ID!) {
    account(id: $id) {
      id
      name
      publicSlug
      publicSiteUrl
    }
    coworkMcpConnector(accountId: $id) {
      dataSourceId
      mcpUrl
      hasCredentials
      lastUsedAt
    }
    tinycmsStarter(accountId: $id)
  }
`;

const SIGNUPS_OPEN = gql`
  query SignupsOpen {
    signupsOpen
  }
`;

const VERIFY_SIGNUP_EMAIL = gql`
  mutation VerifySignupEmail($email: String!, $code: String!) {
    verifySignupEmail(email: $email, code: $code)
  }
`;

const SLUG_AVAILABLE = gql`
  query PublicSlugAvailable($slug: String!, $accountId: ID) {
    publicSlugAvailable(slug: $slug, accountId: $accountId) {
      slug
      available
      reason
    }
  }
`;

interface Credentials {
  mcpUrl: string;
  clientId: string;
  clientSecret: string;
  dataSourceId?: string;
}

const stripePromise: Promise<Stripe | null> = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ''
);
const isStripeTestMode =
  !process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY.startsWith('pk_test_');

/** The marketing site: www.tinycms.ai in production, the same origin locally (dev edge). */
export const MARKETING_ORIGIN = window.location.hostname.endsWith('tinycms.ai')
  ? 'https://www.tinycms.ai'
  : '';

const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,62}$/;

/**
 * Each invited person has their own one-time code (e.g. "parsnip-4821"), emailed
 * from the ops portal. A staff invite can grant a complimentary paid plan (no
 * card). The field checks the code with the API (which doesn't use it up); signup
 * uses it up. A code is only optional with TINYCMS_SIGNUP_INVITE_ONLY=off on the
 * API: with the switch on, signing up without one is refused.
 */
const backendHost = process.env.REACT_APP_BACKEND_HOST || 'http://localhost:4000';

async function inviteCodeWorks(code: string): Promise<{ ok: boolean; grantPlan: string | null }> {
  const res = await fetch(`${backendHost}/api/access-requests/check-invite`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || 'Couldn’t check that code. Please try again.');
  return {
    ok: Boolean(body?.ok),
    grantPlan:
      body?.grantPlan === 'personal' || body?.grantPlan === 'business' || body?.grantPlan === 'pro'
        ? body.grantPlan
        : null,
  };
}

/** "Rosa's Bakery" → "rosas-bakery". */
function toSlug(name: string) {
  return name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63);
}

/** Back to the top of the page. Auth pages scroll the body, not the window (useAuthPage). */
function scrollToTop() {
  window.scrollTo(0, 0);
  document.body.scrollTo?.(0, 0);
  document.getElementById('root')?.scrollTo?.(0, 0);
}

const longDate = (d: Date) =>
  d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

export function FlowHeader({ right }: { right?: React.ReactNode }) {
  return (
    <header className="flow-header">
      <a className="flow-brand" href={`${MARKETING_ORIGIN}/`}>
        <SwallowMark className="flow-mark" />
        <TinycmsWordmark />
      </a>
      {right}
    </header>
  );
}

export function TinycmsSignupFlow() {
  useAuthPage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isLoading: authLoading, login } = useAuth();
  const { accountId, selectedAccount, refetchAccounts } = useAccount();
  const { plans, loading: plansLoading } = useTinycmsPlans();

  // Someone already signed in is finishing setup, not signing up. Decided
  // once, when auth settles, so signing up mid-flow doesn't count.
  const [resuming, setResuming] = useState<boolean | null>(null);
  useEffect(() => {
    if (!authLoading && resuming === null) setResuming(isAuthenticated);
  }, [authLoading, isAuthenticated, resuming]);

  const linkPlan = searchParams.get('plan');
  const urlPlan = linkPlan === 'free' ? null : linkPlan;
  // The page sign-in was sending this person back to (an upload link…).
  const back = searchParams.get('next');
  const [step, setStep] = useState<Step>(urlPlan ? 'account' : 'plan');
  const [planKey, setPlanKey] = useState(urlPlan || 'personal');
  const [interval, setInterval] = useState<BillingInterval>(
    toInterval(searchParams.get('interval'))
  );

  // Account
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  // Optional: the code is typed, or comes with the link (?invite=).
  const linkInvite = (searchParams.get('invite') || '').trim();
  const [inviteFromLink, setInviteFromLink] = useState(false);
  const [inviteCode, setInviteCode] = useState(linkInvite);
  const [inviteOpen, setInviteOpen] = useState(Boolean(linkInvite));
  const [inviteOk, setInviteOk] = useState(false);
  const [grantPlan, setGrantPlan] = useState<string | null>(null);
  const [checkingInvite, setCheckingInvite] = useState(false);
  // A link's code decides the first step, so nothing shows until it's checked.
  const [linkPending, setLinkPending] = useState(Boolean(linkInvite));
  // New accounts paused (the API's daily cap): known as the page opens, or found
  // out at the end if the last place went while they were filling this in. An
  // API that doesn't know the question yet leaves signup open.
  const { data: signupsData, loading: signupsLoading } = useQuery<{ signupsOpen: boolean }>(
    SIGNUPS_OPEN,
    { skip: resuming !== false, fetchPolicy: 'network-only' }
  );
  const [refused, setRefused] = useState<{ cardEntered: boolean } | null>(null);
  const [pausedEnterCode, setPausedEnterCode] = useState(Boolean(linkInvite));
  // Account is two screens: the details, then the emailed code (UX review v9).
  const [acctPhase, setAcctPhase] = useState<'details' | 'code'>('details');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [signup, { loading: creatingAccount }] = useMutation<{ signup: string }>(SIGNUP_MUTATION);
  const [created, setCreated] = useState(false);
  // Email confirmation: the code step, then the server's proof (a token).
  const [codeSentTo, setCodeSentTo] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [emailToken, setEmailToken] = useState<{ email: string; token: string } | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const [requestEmailCode, { loading: sendingCode }] = useMutation(REQUEST_EMAIL_CODE);
  const [verifySignupEmail, { loading: checkingCode }] = useMutation<{
    verifySignupEmail: string;
  }>(VERIFY_SIGNUP_EMAIL);
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);
  const emailConfirmed = emailToken !== null && emailToken.email === email.trim().toLowerCase();
  // A code is out for the address typed now (an empty address never counts,
  // or the button would read "Continue" until the first letter).
  const codeSent = Boolean(codeSentTo) && codeSentTo === email.trim().toLowerCase();

  // Site address
  const [addr, setAddr] = useState('');
  const [addrTouched, setAddrTouched] = useState(false);

  // Arriving from sign-in keeps its scroll position otherwise.
  useEffect(() => {
    scrollToTop();
  }, []);

  // An unknown ?plan= falls back to Personal once the plans arrive.
  useEffect(() => {
    if (plans.length && !plans.some((p) => p.key === planKey)) setPlanKey('personal');
  }, [plans, planKey]);

  // Resuming: the account's own plan, name and progress decide the step.
  const accountPlanKey = selectedAccount?.products?.find((p) => p.product === 'tinycms')?.plan;
  useEffect(() => {
    if (resuming && accountPlanKey) setPlanKey(accountPlanKey);
  }, [resuming, accountPlanKey]);

  const { data: state, refetch: refetchState } = useQuery(SIGNUP_STATE, {
    variables: { id: accountId },
    skip: !isAuthenticated || !accountId,
    fetchPolicy: 'network-only',
  });
  const account = state?.account;
  const connector = state?.coworkMcpConnector;
  // A site with an address but no pages and no starter yet is at Starting point.
  const starterState = state?.tinycmsStarter as
    | { id: string | null; pageCount: number }
    | undefined;
  const needsStarter = Boolean(starterState && !starterState.id && starterState.pageCount === 0);
  // The New site modal's "I'll do it later" skips the agent after the starter.
  const skipAgent = searchParams.get('agent') === 'later';
  const decided = useRef(false);
  useEffect(() => {
    if (!resuming || decided.current || !account) return;
    decided.current = true;
    if (!name && account.name && !account.name.includes('@')) {
      setName(account.name);
      setAddr(toSlug(account.name));
    }
    if (!account.publicSlug) setStep('address');
    else if (needsStarter) setStep('starter');
    else if (!connector?.lastUsedAt && !skipAgent) setStep('agent');
    else navigate('/today', { replace: true });
  }, [resuming, account, connector, needsStarter, skipAgent, name, navigate]);

  const complimentary = Boolean(grantPlan);
  const plan: TinycmsPlan | undefined = plans.find((p) => p.key === planKey);
  const price = plan ? planPrice(plan, interval) : 0;
  const free = complimentary || (plan ? price === 0 : planKey === 'free');
  const steps: Step[] = complimentary
    ? ['account', 'address', 'starter', 'agent']
    : free
      ? ['plan', 'account', 'address', 'starter', 'agent']
      : ['plan', 'account', 'payment', 'address', 'starter', 'agent'];
  const idx = Math.max(0, steps.indexOf(step));
  // Signed in: the plan, account and card are settled from here on.
  const accountMade = Boolean(resuming) || created;

  const trialDays = plan?.trialPeriodDays || 0;
  const trialEnd = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + trialDays);
    return d;
  }, [trialDays]);

  const go = (next: Step) => {
    setErrors({});
    setStep(next);
    scrollToTop();
  };

  /** No accepted code any more. A plan the invite settled is theirs to choose again. */
  const dropInvite = () => {
    setInviteOk(false);
    if (!grantPlan) return;
    setGrantPlan(null);
    setPlanKey(urlPlan || 'personal');
    if (!urlPlan) setStep('plan');
  };

  /** Creates the account (with the saved card on a paid plan), then signs in. */
  const createAccount = async (paymentMethod?: string, verifiedToken?: string) => {
    const emailVerification = verifiedToken || (emailConfirmed ? emailToken?.token : undefined);
    if (!plan) return false;
    try {
      await signup({
        variables: {
          email,
          ...(emailVerification ? { emailVerification } : {}),
          accountName: name.trim(),
          plan: grantPlan || plan.key,
          products: ['tinycms'],
          billingInterval: interval,
          inviteCode: inviteCode.trim(),
          ...(paymentMethod ? { paymentName: email, paymentMethod } : {}),
        },
      });
      login();
      setCreated(true);
      if (!addrTouched) setAddr(toSlug(name));
      go('address');
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : '';
      if (/paused/i.test(message)) {
        setRefused({ cardEntered: Boolean(paymentMethod) });
        scrollToTop();
        return false;
      }
      // The code was used in the meantime, or was sent to another address:
      // back to the invite field, so a complimentary plan isn't silently lost.
      if (/invite/i.test(message)) {
        dropInvite();
        setInviteOpen(true);
        setAcctPhase('details');
        go('account');
        setErrors({ invite: message });
        return false;
      }
      setErrors({
        submit: /already exists/i.test(message)
          ? 'exists'
          : paymentMethod
            ? 'Sign up failed. Please try again. Your card was not charged.'
            : 'Sign up failed. Please try again.',
      });
      return false;
    }
  };

  const errorText = (err: unknown, fallback: string) =>
    err instanceof Error && err.message ? err.message : fallback;

  const sendEmailCode = async () => {
    try {
      await requestEmailCode({ variables: { email: email.trim(), purpose: 'sign_up' } });
      setCodeSentTo(email.trim().toLowerCase());
      setEmailCode('');
      setCooldown(RESEND_SECONDS);
      setErrors((x) => ({ ...x, email: '', code: '' }));
      setAcctPhase('code');
    } catch (err) {
      setErrors((x) => ({ ...x, email: errorText(err, 'The code couldn’t be sent. Try again.') }));
    }
  };

  const applyInvite = async (code = inviteCode, { fromLink = false } = {}) => {
    const fail = (invite: string) => {
      setErrors((x) => ({ ...x, invite }));
      return false;
    };
    if (!code.trim()) return fail('Add your invite code');
    setCheckingInvite(true);
    try {
      const info = await inviteCodeWorks(code.trim());
      if (!info.ok) {
        return fail(
          'That code isn’t valid. Check for typos, or ask whoever sent it for a new one.'
        );
      }
      setGrantPlan(info.grantPlan);
      if (info.grantPlan) {
        // The plan is settled by the invite: straight to Account, no card.
        setPlanKey(info.grantPlan);
        setStep((s) => (s === 'plan' || s === 'payment' ? 'account' : s));
      }
    } catch (err) {
      return fail(errorText(err, 'Couldn’t check that code. Please try again.'));
    } finally {
      setCheckingInvite(false);
    }
    setInviteOk(true);
    setInviteFromLink(fromLink);
    setErrors((x) => ({ ...x, invite: '' }));
    return true;
  };

  // A code that came with the link is checked once, as the page opens.
  const linkChecked = useRef(false);
  useEffect(() => {
    if (linkInvite && !linkChecked.current) {
      linkChecked.current = true;
      applyInvite(linkInvite, { fromLink: true }).finally(() => setLinkPending(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkInvite]);

  const submitAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    if (acctPhase === 'details') {
      // A code typed but not applied yet: apply it and stop here, so they see
      // what it did (a complimentary plan drops Payment) before going on.
      if (inviteCode.trim() && !inviteOk) {
        await applyInvite();
        return;
      }
      const next: Record<string, string> = {};
      if (!name.trim()) next.name = 'Add a name for your site';
      if (!email) next.email = 'Add your email';
      else if (!/\S+@\S+\.\S+/.test(email)) next.email = 'That doesn’t look like an email address';
      setErrors(next);
      if (Object.keys(next).length) return;
      // Already confirmed this address (came back from Payment): straight on.
      if (emailConfirmed) {
        if (free) await createAccount();
        else go('payment');
        return;
      }
      // A code is still out for this address: back to it, else send one.
      if (codeSent) setAcctPhase('code');
      else await sendEmailCode();
      return;
    }

    // The code screen.
    if (!emailConfirmed) {
      if (emailCode.replace(/\D/g, '').length !== 6) {
        setErrors({ code: 'Enter the 6-digit code from the email' });
        return;
      }
      try {
        const { data } = await verifySignupEmail({
          variables: { email: email.trim(), code: emailCode },
        });
        if (!data?.verifySignupEmail) return;
        setEmailToken({ email: email.trim().toLowerCase(), token: data.verifySignupEmail });
        if (free) await createAccount(undefined, data.verifySignupEmail);
        else go('payment');
      } catch (err) {
        setErrors({ code: errorText(err, 'That code didn’t work.') });
      }
      return;
    }
    if (free) await createAccount();
    else go('payment');
  };

  // Auth settles before the flow decides; a signed-in visit waits for its account.
  if (
    resuming === null ||
    (resuming && !account) ||
    (plansLoading && !plans.length) ||
    linkPending ||
    signupsLoading
  ) {
    return (
      <div className="flow">
        <FlowHeader />
        <Loader label="Loading" className="flow-loading" />
      </div>
    );
  }

  // Logo only until the account exists; then whose account this is.
  const headerRight =
    accountMade && email ? <span className="flow-header-note">{email}</span> : null;
  // "Log in" sits under the primary button until the account exists.
  const toLogin = () => navigate(loginPathFor(back));

  const stepOf = `Step ${idx + 1} of ${steps.length} · ${STEP_LABELS[step]}`;
  const wide = step === 'starter' ? 'true' : undefined;

  const inviteField = (
    <InviteField
      open={inviteOpen}
      code={inviteCode}
      accepted={
        !inviteOk
          ? null
          : complimentary
            ? `Invite accepted · complimentary ${plan?.label || grantPlan}${
                grantPlan === 'pro' ? ' for one year' : ''
              }`
            : inviteFromLink || inviteCode.includes('@')
              ? 'Invite accepted'
              : `Invite accepted · ${inviteCode.trim()}`
      }
      checking={checkingInvite}
      error={errors.invite}
      onOpen={() => setInviteOpen(true)}
      onCode={(v) => {
        setInviteCode(v);
        setErrors((x) => ({ ...x, invite: '' }));
      }}
      onApply={() => void applyInvite()}
      onChange={() => dropInvite()}
      onCancel={() => {
        setInviteOpen(false);
        setPausedEnterCode(false);
        setInviteCode('');
        setErrors((x) => ({ ...x, invite: '' }));
      }}
    />
  );

  // Paused: request an invite, or enter a code they already have (it still works).
  if (!accountMade && !inviteOk && (signupsData?.signupsOpen === false || refused)) {
    return (
      <div className="flow">
        <FlowHeader />
        <main className="flow-main">
          <section className="flow-section">
            {pausedEnterCode ? (
              <div className="flow-gate">
                <h1 className="flow-h1">Enter your invite code</h1>
                <p className="flow-lede">Invite codes still work while new accounts are paused.</p>
                {inviteField}
                <div className="flow-gate-links">
                  <button
                    type="button"
                    className="flow-link"
                    onClick={() => setPausedEnterCode(false)}
                  >
                    Request an invite
                  </button>
                  <button type="button" className="flow-link" onClick={toLogin}>
                    Already have an account? Log in
                  </button>
                </div>
              </div>
            ) : (
              <RequestAccess
                initialEmail={email}
                cardNotCharged={Boolean(refused?.cardEntered)}
                onEnterCode={() => {
                  setInviteOpen(true);
                  setPausedEnterCode(true);
                }}
                onLogin={toLogin}
              />
            )}
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className="flow">
      <FlowHeader right={headerRight} />
      <main className="flow-main">
        {/* One column per step: its bar segment over its label (labels hide on phones). */}
        <nav className="flow-steps" data-wide={wide} aria-label="Signup steps">
          {steps.map((s, i) => {
            const done = i < idx;
            // Before the account exists any earlier step can be revisited.
            const canGo = done && !accountMade;
            return (
              <button
                key={s}
                type="button"
                className="flow-steps-item"
                data-state={done ? 'done' : i === idx ? 'now' : 'next'}
                aria-current={i === idx ? 'step' : undefined}
                disabled={!canGo}
                onClick={() => canGo && go(s)}
              >
                <span className="flow-steps-seg" />
                <span className="flow-steps-label">{STEP_LABELS[s]}</span>
              </button>
            );
          })}
        </nav>

        <section className="flow-section" data-wide={wide}>
          {step === 'plan' ? (
            <PlanStep
              stepOf={stepOf}
              plans={plans}
              planKey={planKey}
              onPlan={setPlanKey}
              interval={interval}
              onInterval={setInterval}
              onContinue={() => {
                // A code typed but not applied: apply it and stop, as on Account.
                if (inviteCode.trim() && !inviteOk) void applyInvite();
                else go('account');
              }}
            >
              {inviteField}
            </PlanStep>
          ) : null}

          {step === 'account' && acctPhase === 'details' ? (
            <form onSubmit={submitAccount} noValidate>
              <div className="flow-step-of">{stepOf}</div>
              <h1 className="flow-h1">Create your account</h1>
              <p className="flow-lede">
                You sign in with just your email. We send you a code each time, so there’s no
                password to remember.
              </p>

              <div className="flow-fields flow-acct-fields">
                <label className="flow-field">
                  <span className="flow-label">Site or company name</span>
                  <input
                    className="flow-input"
                    data-error={errors.name ? 'true' : undefined}
                    value={name}
                    placeholder="Site or company name"
                    autoComplete="organization"
                    onChange={(e) => {
                      setName(e.target.value);
                      setErrors((x) => ({ ...x, name: '' }));
                    }}
                  />
                  {errors.name ? <span className="flow-error">{errors.name}</span> : null}
                </label>
                <label className="flow-field">
                  <span className="flow-label">Email</span>
                  <input
                    className="flow-input"
                    data-error={errors.email ? 'true' : undefined}
                    type="email"
                    value={email}
                    placeholder="Email"
                    autoComplete="email"
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setErrors((x) => ({ ...x, email: '', submit: '' }));
                    }}
                  />
                  {errors.email ? <span className="flow-error">{errors.email}</span> : null}
                </label>
              </div>
              {inviteField}
              <SubmitError
                error={errors.submit}
                email={email}
                onLogin={() => navigate(loginPathFor(back))}
              />
              <button type="submit" className="flow-btn" disabled={sendingCode || checkingInvite}>
                {sendingCode ? 'Sending a code…' : 'Continue'}
              </button>
              <p className="flow-fine">
                Next, we’ll email you a 6-digit code to confirm it’s you.
                <br />
                By continuing you agree to the{' '}
                <a href="https://www.tinycms.ai/terms" target="_blank" rel="noopener noreferrer">
                  Terms of Service
                </a>{' '}
                and{' '}
                <a href="https://www.tinycms.ai/privacy" target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                </a>
                .
              </p>
            </form>
          ) : null}

          {step === 'account' && acctPhase === 'code' ? (
            <form onSubmit={submitAccount} noValidate>
              <div className="flow-step-of">{stepOf}</div>
              <h1 className="flow-h1">Check your email</h1>
              <p className="flow-lede">
                We sent a 6-digit code to <strong className="flow-strong">{codeSentTo}</strong>. It
                works for 10 minutes.
              </p>
              <input
                className="flow-input flow-code-big"
                data-error={errors.code ? 'true' : undefined}
                value={emailCode}
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="XXXXXX"
                aria-label="6-digit code"
                autoFocus
                onChange={(e) => {
                  // No maxLength: it would cut a pasted "196 391" to "196 39" before the digits are taken.
                  setEmailCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                  setErrors((x) => ({ ...x, code: '' }));
                }}
              />
              {errors.code ? <div className="flow-error flow-code-error">{errors.code}</div> : null}
              <div className="flow-code-actions">
                <button
                  type="button"
                  className="flow-link flow-resend"
                  disabled={cooldown > 0 || sendingCode}
                  onClick={() => void sendEmailCode()}
                >
                  {cooldown > 0 ? `Send a new code in ${cooldown}s` : 'Send a new code'}
                </button>
                <button type="button" className="flow-link" onClick={() => setAcctPhase('details')}>
                  Use a different email
                </button>
              </div>
              <SubmitError
                error={errors.submit}
                email={email}
                onLogin={() => navigate(loginPathFor(back))}
              />
              <button
                type="submit"
                className="flow-btn"
                data-dim={emailCode.length === 6 ? undefined : 'true'}
                disabled={creatingAccount || checkingCode}
              >
                {creatingAccount
                  ? 'Creating your account…'
                  : checkingCode
                    ? 'Checking…'
                    : free
                      ? 'Create free account'
                      : 'Continue to payment'}
              </button>
              <p className="flow-fine">
                Each time you sign in, we’ll email you a new code. Prefer a password? Add one later
                in Account → Profile.
              </p>
            </form>
          ) : null}

          {step === 'payment' && plan ? (
            <Elements stripe={stripePromise}>
              <PaymentStep
                stepOf={stepOf}
                plan={plan}
                interval={interval}
                email={email}
                emailVerification={emailConfirmed ? emailToken?.token : undefined}
                trialEnd={trialEnd}
                busy={creatingAccount}
                submitError={errors.submit}
                onLogin={() => navigate(loginPathFor(back))}
                onCard={createAccount}
              />
            </Elements>
          ) : null}

          {step === 'address' ? (
            <AddressStep
              stepOf={stepOf}
              free={free}
              accountId={accountId}
              siteName={name.trim() || account?.name || ''}
              addr={addr}
              onAddr={(v) => {
                setAddr(v);
                setAddrTouched(true);
              }}
              onSaved={async () => {
                await Promise.all([refetchState(), refetchAccounts()]);
                go('starter');
              }}
            />
          ) : null}

          {step === 'starter' ? (
            <StarterStep
              stepOf={stepOf}
              accountId={accountId}
              siteName={name.trim() || account?.name || ''}
              siteAddr={account?.publicSlug || addr}
              onDone={async () => {
                await refetchState();
                if (skipAgent) {
                  scrollToTop();
                  navigate(takeReturnPath(window.location.search) || '/today');
                } else go('agent');
              }}
            />
          ) : null}

          {step === 'agent' ? (
            <AgentStep
              stepOf={stepOf}
              accountId={accountId}
              siteUrl={account?.publicSiteUrl}
              existingConnector={connector}
              onDone={() => {
                scrollToTop();
                navigate(takeReturnPath(window.location.search) || '/today');
              }}
            />
          ) : null}

          {!accountMade ? (
            <div className="flow-gate-links" data-single="true">
              <button type="button" className="flow-link" onClick={toLogin}>
                Already have an account? Log in
              </button>
            </div>
          ) : null}
        </section>

        {/* Under the step, same column. The starting point takes the width: no plan aside there. */}
        {step !== 'plan' && step !== 'starter' && plan ? (
          <aside className="flow-aside">
            <div className="flow-aside-head">
              <span>Your plan</span>
              {!accountMade && !complimentary ? (
                <button type="button" className="flow-link" onClick={() => go('plan')}>
                  Change
                </button>
              ) : null}
            </div>
            <div className="flow-aside-plan">{plan.label}</div>
            {!resuming ? (
              <>
                <div className="flow-aside-price">
                  {complimentary
                    ? grantPlan === 'pro'
                      ? 'Complimentary for one year, no card needed'
                      : 'Complimentary, no card needed'
                    : free
                      ? 'Free, no card needed'
                      : billingLine(plan, interval)}
                </div>
                {!free && !complimentary && trialDays ? (
                  <div className="flow-aside-trial">
                    Free until {longDate(trialEnd)}. Then {chargeLine(plan, interval)}.
                  </div>
                ) : null}
              </>
            ) : null}
          </aside>
        ) : null}
      </main>
    </div>
  );
}

/**
 * The optional invite code, under Plan and Account: a quiet link until asked
 * for, then the field, then a green row once accepted. Not its own form (it sits
 * inside the step's), so Enter applies the code here rather than moving on.
 */
function InviteField({
  open,
  code,
  accepted,
  checking,
  error,
  onOpen,
  onCode,
  onApply,
  onChange,
  onCancel,
}: {
  open: boolean;
  code: string;
  accepted: string | null;
  checking: boolean;
  error?: string;
  onOpen: () => void;
  onCode: (code: string) => void;
  onApply: () => void;
  onChange: () => void;
  onCancel: () => void;
}) {
  if (accepted) {
    return (
      <div className="flow-invite-ok">
        <span>✓</span>
        <span>{accepted}</span>
        <button type="button" className="flow-invite-change" onClick={onChange}>
          Change
        </button>
      </div>
    );
  }
  if (!open) {
    return (
      <div className="flow-invite">
        <button type="button" className="flow-link" onClick={onOpen}>
          Have an invite code?
        </button>
      </div>
    );
  }
  return (
    <div className="flow-invite">
      <div className="flow-label-row">
        <label className="flow-label" htmlFor="flow-invite-code">
          Invite code
        </label>
        <button type="button" className="flow-link" onClick={onCancel}>
          Cancel
        </button>
      </div>
      <div className="flow-invite-row">
        <input
          id="flow-invite-code"
          className="flow-input flow-invite-code"
          data-error={error ? 'true' : undefined}
          value={code}
          placeholder="XXXX-XXXX"
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          autoFocus
          onChange={(e) => onCode(e.target.value.toUpperCase())}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (!checking) onApply();
            }
          }}
        />
        <button
          type="button"
          className="flow-btn flow-btn-secondary flow-invite-apply"
          disabled={checking}
          onClick={onApply}
        >
          {checking ? 'Checking…' : 'Apply'}
        </button>
      </div>
      {error ? (
        <span className="flow-error" role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}

function SubmitError({
  error,
  email,
  onLogin,
}: {
  error?: string;
  email: string;
  onLogin: () => void;
}) {
  if (!error) return null;
  return (
    <p className="flow-error flow-submit-error">
      {error === 'exists' ? (
        <>
          An account with {email} already exists.{' '}
          <button type="button" className="flow-link" onClick={onLogin}>
            Log in instead
          </button>
        </>
      ) : (
        error
      )}
    </p>
  );
}

function IntervalToggle({
  interval,
  onInterval,
}: {
  interval: BillingInterval;
  onInterval: (v: BillingInterval) => void;
}) {
  return (
    <div className="flow-seg" role="radiogroup" aria-label="Billing">
      {(['month', 'year'] as BillingInterval[]).map((v) => (
        <button
          key={v}
          type="button"
          role="radio"
          aria-checked={interval === v}
          data-active={interval === v ? 'true' : undefined}
          onClick={() => onInterval(v)}
        >
          {v === 'month' ? 'Monthly' : 'Yearly'}
          {v === 'year' ? <span className="flow-save">Save 25%</span> : null}
        </button>
      ))}
    </div>
  );
}

function PlanStep({
  stepOf,
  plans,
  planKey,
  onPlan,
  interval,
  onInterval,
  onContinue,
  children,
}: {
  stepOf: string;
  plans: TinycmsPlan[];
  planKey: string;
  onPlan: (key: string) => void;
  interval: BillingInterval;
  onInterval: (v: BillingInterval) => void;
  onContinue: () => void;
  children?: React.ReactNode;
}) {
  const selected = plans.find((p) => p.key === planKey);
  const trial = Math.max(0, ...plans.map((p) => p.trialPeriodDays || 0));
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onContinue();
      }}
    >
      <div className="flow-step-of">{stepOf}</div>
      <h1 className="flow-h1">Choose a plan</h1>
      <p className="flow-lede">
        {trial
          ? `Start free, or try any paid plan free for ${trial} days. Switch at any time.`
          : 'Start free, or pick a paid plan. Switch at any time.'}
      </p>
      <IntervalToggle interval={interval} onInterval={onInterval} />
      <div className="flow-plans" role="radiogroup" aria-label="Plan">
        {plans.map((p) => {
          const on = p.key === planKey;
          const pr = planPrice(p, interval);
          return (
            <div
              key={p.key}
              role="radio"
              aria-checked={on}
              tabIndex={0}
              className="flow-plan"
              data-active={on ? 'true' : undefined}
              onClick={() => onPlan(p.key)}
              onKeyDown={(e) => {
                if (e.key === ' ' || e.key === 'Enter') {
                  e.preventDefault();
                  onPlan(p.key);
                }
              }}
            >
              <div className="flow-plan-row">
                <span className="flow-radio" />
                <div className="flow-plan-text">
                  <div className="flow-plan-name">{p.label}</div>
                  <div className="flow-plan-summary">{p.summary}</div>
                  <div className="flow-plan-sub">
                    {!pr
                      ? 'No card needed'
                      : `${interval === 'year' ? `${money(pr * 12)} billed yearly` : 'Billed monthly'}${
                          p.trialPeriodDays ? ` · ${p.trialPeriodDays}-day trial` : ''
                        }`}
                  </div>
                </div>
                <div className="flow-plan-price">
                  {money(pr)}
                  <span>/mo</span>
                </div>
              </div>
              {on ? (
                <ul className="flow-features">
                  {p.features.map((f) => (
                    <li key={f}>
                      <span aria-hidden="true">✓</span>
                      {f}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          );
        })}
      </div>
      {children}
      <button type="submit" className="flow-btn" disabled={!selected}>
        {selected ? `Continue with ${selected.label}` : 'Continue'}
      </button>
    </form>
  );
}

function PaymentStep({
  stepOf,
  plan,
  interval,
  email,
  emailVerification,
  trialEnd,
  busy,
  submitError,
  onLogin,
  onCard,
}: {
  stepOf: string;
  plan: TinycmsPlan;
  interval: BillingInterval;
  email: string;
  /** The confirmed-email token: lets this step ask for a SetupIntent before an account exists. */
  emailVerification?: string;
  trialEnd: Date;
  busy: boolean;
  submitError?: string;
  onLogin: () => void;
  onCard: (paymentMethod: string) => Promise<boolean>;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [createSetupIntent] = useMutation<{ setupIntent: { clientSecret: string } }>(
    CREATE_SETUP_INTENT
  );
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [cardError, setCardError] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  const fetchSetupIntent = useCallback(async () => {
    setLoadFailed(false);
    try {
      const { data } = await createSetupIntent({
        variables: { email: email.trim().toLowerCase(), emailVerification },
      });
      setClientSecret(data?.setupIntent?.clientSecret || null);
      if (!data?.setupIntent?.clientSecret) setLoadFailed(true);
    } catch {
      setLoadFailed(true);
    }
  }, [createSetupIntent, email, emailVerification]);

  useEffect(() => {
    void fetchSetupIntent();
  }, [fetchSetupIntent]);

  const cardStyle = useMemo(() => {
    const css = getComputedStyle(document.documentElement);
    const v = (name: string, fallback: string) => css.getPropertyValue(name).trim() || fallback;
    return {
      style: {
        base: {
          fontSize: '16px',
          color: v('--ink-ink', '#0d0f12'),
          fontFamily: 'Geist, system-ui, sans-serif',
          '::placeholder': { color: v('--ink-ink3', '#7a8190') },
        },
        invalid: { color: v('--ink-red', '#d33'), iconColor: v('--ink-red', '#d33') },
      },
      hidePostalCode: true,
    };
  }, []);

  const trialDays = plan.trialPeriodDays || 0;
  const charge = chargeLine(plan, interval);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements || !clientSecret) return;
    const card = elements.getElement(CardElement);
    if (!card) return;
    setCardError('');
    setConfirming(true);
    try {
      const { error, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: { card, billing_details: { email } },
      });
      if (error) {
        setCardError(error.message || 'Your card couldn’t be saved. Try again.');
        return;
      }
      if (setupIntent?.status === 'succeeded' && setupIntent.payment_method) {
        const ok = await onCard(setupIntent.payment_method as string);
        // The confirmed intent is used up; a retry needs a fresh one.
        if (!ok) void fetchSetupIntent();
      }
    } finally {
      setConfirming(false);
    }
  };

  const working = confirming || busy;
  return (
    <form onSubmit={submit} noValidate>
      <div className="flow-step-of">{stepOf}</div>
      <h1 className="flow-h1">{trialDays ? 'Start your free trial' : 'Add your card'}</h1>
      <p className="flow-lede">
        {trialDays
          ? `Add a card to try ${plan.label}. You won’t be charged until ${longDate(trialEnd)}, and you can cancel before then.`
          : `Add a card for ${plan.label}.`}
      </p>
      {isStripeTestMode ? (
        <div className="flow-test">
          <strong>Test mode.</strong> Use 4242 4242 4242 4242, any future date and any CVC.
        </div>
      ) : null}
      <div className="flow-field" style={{ marginTop: 20 }}>
        <span className="flow-label">Card</span>
        {loadFailed ? (
          <p className="flow-error">
            The card form didn’t load.{' '}
            <button type="button" className="flow-link" onClick={() => void fetchSetupIntent()}>
              Try again
            </button>
          </p>
        ) : (
          <div className="flow-card" data-error={cardError ? 'true' : undefined}>
            <CardElement options={cardStyle} onChange={() => setCardError('')} />
          </div>
        )}
        {cardError ? <span className="flow-error">{cardError}</span> : null}
      </div>
      <div className="flow-due">
        <span>Due today</span>
        <strong>{trialDays ? '$0.00' : charge}</strong>
      </div>
      <SubmitError error={submitError} email={email} onLogin={onLogin} />
      <button type="submit" className="flow-btn" disabled={working || !stripe || !clientSecret}>
        {working
          ? trialDays
            ? 'Starting your trial…'
            : 'Saving…'
          : trialDays
            ? `Start ${trialDays}-day free trial`
            : 'Continue'}
      </button>
      {trialDays ? (
        <p className="flow-fine">
          On {longDate(trialEnd)} you’ll be charged {charge}, then{' '}
          {interval === 'year' ? 'yearly' : 'monthly'} until you cancel in Account › Billing. Free
          trials are one per person: sites you add later are billed from the day you add them.
        </p>
      ) : null}
    </form>
  );
}

function AddressStep({
  stepOf,
  free,
  accountId,
  siteName,
  addr,
  onAddr,
  onSaved,
}: {
  stepOf: string;
  free: boolean;
  accountId?: string;
  siteName: string;
  addr: string;
  onAddr: (v: string) => void;
  onSaved: () => Promise<void>;
}) {
  const [check, { data, loading: checking }] = useLazyQuery(SLUG_AVAILABLE, {
    fetchPolicy: 'network-only',
  });
  const [updatePublicSite, { loading: saving }] = useMutation(UPDATE_PUBLIC_SITE);
  const [saveError, setSaveError] = useState('');

  // Ask the server once typing pauses.
  useEffect(() => {
    if (!SLUG_RE.test(addr)) return;
    const t = setTimeout(() => {
      void check({ variables: { slug: addr, accountId: accountId || null } });
    }, 300);
    return () => clearTimeout(t);
  }, [addr, accountId, check]);

  const result = data?.publicSlugAvailable;
  const current = result && result.slug === addr ? result : null;
  const host = `${addr}.tinycms.ai`;
  let tone: 'ok' | 'error' | undefined;
  let status: string;
  if (!addr) status = 'Letters, numbers and hyphens';
  else if (addr.length < 2) status = 'At least 2 characters';
  else if (!SLUG_RE.test(addr)) status = 'Start with a letter or number';
  else if (!current || checking) status = 'Checking…';
  else if (current.available) {
    tone = 'ok';
    status = `${host} is available`;
  } else {
    tone = 'error';
    status = current.reason === 'reserved' ? `${addr} is reserved` : `${host} is taken`;
  }
  const ok = tone === 'ok';

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ok || !accountId) return;
    setSaveError('');
    try {
      await updatePublicSite({
        variables: {
          accountId,
          input: { publicSlug: addr, publicTitle: siteName || addr, publicEnabled: true },
        },
      });
      await onSaved();
    } catch (err) {
      const message = err instanceof Error ? err.message : '';
      setSaveError(
        /already in use/i.test(message)
          ? `${host} was just taken. Try another.`
          : /reserved/i.test(message)
            ? `${addr} is reserved. Try another.`
            : message || 'Your address couldn’t be saved. Try again.'
      );
    }
  };

  return (
    <form onSubmit={submit}>
      <div className="flow-step-of">{stepOf}</div>
      <h1 className="flow-h1">Pick your site address</h1>
      <p className="flow-lede">
        {free
          ? 'This is where your site lives. You can switch to your own web address on a paid plan.'
          : 'Your site goes live here first. You can add your own web address later in Site.'}
      </p>
      <div className="flow-addr">
        <span className="flow-m-hide">https://</span>
        <input
          value={addr}
          placeholder="my-site"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          autoFocus
          aria-label="Site address"
          onChange={(e) =>
            onAddr(
              e.target.value
                .toLowerCase()
                .replace(/[^a-z0-9-]/g, '')
                .slice(0, 63)
            )
          }
        />
        <span>.tinycms.ai</span>
      </div>
      <div className="flow-status-line" data-tone={tone}>
        <span className="flow-status-dot" />
        {status}
      </div>
      {saveError ? <p className="flow-error flow-submit-error">{saveError}</p> : null}
      <button type="submit" className="flow-btn" disabled={!ok || saving || !accountId}>
        {saving ? 'Saving…' : 'Continue'}
      </button>
    </form>
  );
}

function CopyRow({ label, value, secret }: { label: string; value: string; secret?: boolean }) {
  const [copied, setCopied] = useState(false);
  const [shown, setShown] = useState(!secret);
  const multiline = value.includes('\n');
  return (
    <div className="flow-cred">
      <span className="flow-cred-label">{label}</span>
      <div className="flow-cred-row">
        <code className="flow-cred-value" data-multiline={multiline ? 'true' : undefined}>
          {shown ? value : `${value.slice(0, 4)}${'•'.repeat(16)}`}
        </code>
        {secret ? (
          <button type="button" className="flow-cred-reveal" onClick={() => setShown((v) => !v)}>
            {shown ? 'Hide' : 'Show'}
          </button>
        ) : null}
        <button
          type="button"
          className="flow-cred-copy"
          onClick={async (e) => {
            // If the browser refuses, Show reveals the value to select.
            if (await copyToClipboard(value, e.currentTarget)) {
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            }
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

/**
 * What the guides can show. The address is known as soon as the site has one;
 * the key values only while a just-made secret is in memory.
 */
function connectionValues(
  mcpUrl: string,
  creds: Credentials | null
): Partial<Record<ConnectionValue, string>> {
  const values: Partial<Record<ConnectionValue, string>> = {
    url: mcpUrl,
    authUrl: `${mcpUrl}/oauth/authorize`,
    tokenUrl: `${mcpUrl}/oauth/token`,
    scope: 'mcp',
    // VS Code registers itself and opens the sign-in window; no key in the file.
    vscodeConfig: JSON.stringify({ servers: { tinycms: { type: 'http', url: mcpUrl } } }, null, 2),
  };
  if (creds) {
    values.clientId = creds.clientId;
    values.clientSecret = creds.clientSecret;
    // The Client ID is also a bearer token for agents that take one.
    values.token = creds.clientId;
    values.vscodeTokenConfig = JSON.stringify(
      {
        servers: {
          tinycms: {
            type: 'http',
            url: mcpUrl,
            headers: { Authorization: `Bearer ${creds.clientId}` },
          },
        },
      },
      null,
      2
    );
  }
  return values;
}

const SECRET_VALUES: ConnectionValue[] = ['clientId', 'clientSecret', 'token', 'vscodeTokenConfig'];

/** The key values, in the order the Advanced fold lists them. */
const ADVANCED_VALUES: ConnectionValue[] = [
  'clientId',
  'clientSecret',
  'token',
  'authUrl',
  'tokenUrl',
  'scope',
  'vscodeTokenConfig',
];

const SHOW_FIRST_RUN_AGENT_CARD = gql`
  mutation ShowFirstRunAgentCard($accountId: ID!, $agent: String!) {
    showFirstRunAgentCard(accountId: $accountId, agent: $agent)
  }
`;

const STARTING_RULES: Array<[keyof AgentRules, string]> = [
  ['drafts', 'Create & edit drafts'],
  ['publish', 'Publish & schedule'],
  ['surface', 'Change the site surface'],
  ['delete', 'Delete content'],
];
const RULE_WORD = { allow: 'Goes out without asking', ask: 'Ask me first', never: 'Can’t' };

/** How a just-connected agent starts out: its real rules, read-only. */
function StartingRules({
  accountId,
  dataSourceId,
  agentName,
}: {
  accountId: string;
  dataSourceId?: string;
  agentName: string;
}) {
  const { agents } = useAccountAgents(accountId);
  const rules = agents.find((a) => a.dataSourceId === dataSourceId)?.rules;
  if (!rules) return null;
  return (
    <div className="flow-start-card">
      <div className="flow-start-title">How {agentName} starts out</div>
      <p className="flow-start-sub">
        It can write drafts. Anything that would go out or change your site waits for you to approve
        it.
      </p>
      {STARTING_RULES.map(([key, name]) => (
        <div className="flow-start-row" key={key}>
          <span>{name}</span>
          <span className="flow-start-pill" data-rule={rules[key]}>
            {RULE_WORD[rules[key]]}
          </span>
        </div>
      ))}
      <p className="flow-start-foot">
        Every approval, and every change to these, is kept in the Activity Log. When you trust it,
        loosen one action at a time in Account › AI Agents.
      </p>
    </div>
  );
}

export function AgentStep({
  stepOf,
  accountId,
  siteUrl,
  existingConnector,
  onDone,
}: {
  stepOf: string;
  accountId?: string;
  siteUrl?: string | null;
  existingConnector?: {
    dataSourceId?: string;
    mcpUrl: string;
    hasCredentials: boolean;
    lastUsedAt: string | null;
  } | null;
  onDone: () => void;
}) {
  const [creds, setCreds] = useState<Credentials | null>(null);
  const [error, setError] = useState('');
  const [guide, setGuide] = useState<AgentGuide>(AGENT_GUIDES[0]);
  const [connected, setConnected] = useState(false);
  const [createConnector, { loading: creating }] = useMutation(CREATE_COWORK_MCP_CONNECTOR);
  const [rotateConnector, { loading: rotating }] = useMutation(ROTATE_COWORK_MCP_CREDENTIALS);
  const [showFirstRunCard] = useMutation(SHOW_FIRST_RUN_AGENT_CARD);

  // Opening this step makes nothing, so skipping it leaves no agent behind. The
  // agent's sign-in makes the connection (grantForSite); a tool that needs a
  // key asks for one below.
  const mcpUrl = creds?.mcpUrl || existingConnector?.mcpUrl || (siteUrl ? `${siteUrl}/mcp` : '');

  // Connected = the agent has signed in or used the connection (lastUsedAt
  // is stamped on both). Checked every few seconds while waiting.
  const { data: poll, refetch } = useQuery(SIGNUP_STATE, {
    variables: { id: accountId },
    skip: !accountId || !mcpUrl,
    fetchPolicy: 'network-only',
    pollInterval: connected ? 0 : 4000,
  });
  const connector = poll?.coworkMcpConnector || existingConnector;
  useEffect(() => {
    if (mcpUrl && poll?.coworkMcpConnector?.lastUsedAt) setConnected(true);
  }, [mcpUrl, poll]);
  const [checking, setChecking] = useState(false);

  const makeKey = async () => {
    if (!accountId) return;
    setError('');
    const variables = { accountId, label: 'AI agent' };
    try {
      if (connector?.hasCredentials) {
        const { data } = await rotateConnector({ variables });
        setCreds(data?.rotateCoworkMcpCredentials || null);
      } else {
        const { data } = await createConnector({ variables });
        setCreds(data?.createCoworkMcpConnector || null);
      }
      setConnected(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Your key couldn’t be made.');
    }
  };

  const values = mcpUrl ? connectionValues(mcpUrl, creds) : null;
  const agentName = guide.name;

  const row = (v: ConnectionValue) => (
    <CopyRow
      key={v}
      label={VALUE_LABELS[v]}
      value={values?.[v] as string}
      secret={SECRET_VALUES.includes(v)}
    />
  );

  const keyPrompt = (
    <>
      <p className="flow-hint">
        {connector?.hasCredentials
          ? 'Most agents sign you in with your email and never need a key. Your site already has one, and its secret was shown only when it was made. Make a new one to paste into a tool that needs it; any tool using the old one stops working.'
          : 'Most agents sign you in with your email and never need a key. Make one only if your tool asks for it.'}
      </p>
      <button
        type="button"
        className="flow-btn-quiet"
        disabled={creating || rotating}
        onClick={() => void makeKey()}
      >
        {creating || rotating
          ? 'Making…'
          : connector?.hasCredentials
            ? 'Make new connection details'
            : 'Make a key'}
      </button>
    </>
  );

  return (
    <div>
      <div className="flow-step-of">{stepOf}</div>
      <h1 className="flow-h1">Connect your agent</h1>
      <p className="flow-lede">
        Paste your site’s address into your agent, then sign in with your email when it asks. Your
        agent builds your pages and posts through tinycms. It can’t publish anything until you
        approve it. It starts out asking you before anything goes out.
      </p>

      {!mcpUrl ? (
        <p className="flow-lede">Your site needs an address before an agent can connect.</p>
      ) : null}

      {values ? (
        <>
          <div className="flow-tabs" role="tablist" aria-label="Agent">
            {AGENT_GUIDES.map((g) => (
              <button
                key={g.id}
                type="button"
                role="tab"
                aria-selected={g.id === guide.id}
                data-active={g.id === guide.id ? 'true' : undefined}
                onClick={() => setGuide(g)}
              >
                {g.name}
              </button>
            ))}
          </div>
          <ol className="flow-guide">
            {guide.steps.map((s, i) => (
              <li key={`${guide.id}-${i}`}>
                <div>{s.text}</div>
                {s.values?.some((v) => values[v] == null) ? keyPrompt : s.values?.map(row)}
              </li>
            ))}
          </ol>
          {guide.note ? <p className="flow-hint">{guide.note}</p> : null}
          <div className="flow-status" data-tone={connected ? 'ok' : undefined}>
            <span className="flow-status-dot" data-pulse={connected ? undefined : 'true'} />
            <span>
              {connected
                ? `${agentName} is connected. It drafts; you approve what goes out.`
                : checking
                  ? 'Checking…'
                  : `Waiting for ${agentName} to connect`}
            </span>
          </div>
          {connected && accountId ? (
            <StartingRules
              accountId={accountId}
              dataSourceId={creds?.dataSourceId || connector?.dataSourceId}
              agentName={agentName}
            />
          ) : null}
          <details className="flow-advanced">
            <summary>Advanced: for tools that need a key</summary>
            {creds ? (
              <>
                <p className="flow-hint">
                  Most agents sign you in with your email and never need these. For one that asks
                  for a key, paste them here. Keep them private, like a password: the secret is
                  shown only now, and you can make new details in Account › AI Agents.
                </p>
                {ADVANCED_VALUES.map(row)}
              </>
            ) : (
              keyPrompt
            )}
          </details>
        </>
      ) : null}

      {error ? <p className="flow-error flow-submit-error">{error}</p> : null}

      {mcpUrl ? (
        <button
          type="button"
          className="flow-btn"
          disabled={checking}
          onClick={async () => {
            if (connected) {
              // The first-run card on Today. If this fails, Today still opens without it.
              await showFirstRunCard({
                variables: { accountId, agent: agentName },
              }).catch(() => {});
              return onDone();
            }
            setChecking(true);
            try {
              const { data } = await refetch();
              if (data?.coworkMcpConnector?.lastUsedAt) setConnected(true);
            } finally {
              setChecking(false);
            }
          }}
        >
          {connected ? 'Open Today' : checking ? 'Checking…' : 'Check connection'}
        </button>
      ) : null}
      {!connected ? (
        <p className="flow-fine">
          <button type="button" className="flow-link" onClick={onDone}>
            Skip for now
          </button>
          . You can connect one later in Account › AI Agents.
        </p>
      ) : null}
    </div>
  );
}
